import { useEffect } from 'react'

import { useAppSelector } from 'src/Hooks/redux'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { ScheduleSteps } from 'src/Types/ui'
import { spotifyTotalPriceSelector } from 'src/Redux/spotifyCampaignMatchingProcess'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import {
  OrderItemType,
  useGetCurrentOrderQuery,
} from 'src/Redux/artistOrderApi'
import { useFindConflictingOrder } from 'src/Redux/artistOrderApi/useFindConflictingOrder'

import { getIsLanguageActive } from './helpers'

export const getIsPreReleaseCheckoutIncomplete = (
  isPreRelease: boolean,
  releaseDate: string | null,
  hasTrackFile: boolean,
  genres: unknown[],
  languages: Parameters<typeof getIsLanguageActive>[0],
  hasAiUsage: boolean,
): boolean => {
  if (!isPreRelease) {
    return false
  }
  if (!releaseDate || !hasTrackFile) {
    return true
  }
  if (!genres.length || !hasAiUsage) {
    return true
  }
  return getIsLanguageActive(languages)
}

const getShouldRedirectCheckout = (
  step: string | null,
  successPaymentStatus: boolean,
  isSpotifyCampaignInvalid: boolean,
  hasConflictingOrder: boolean,
  isPreReleaseIncomplete: boolean,
): boolean => {
  if (step !== ScheduleSteps.CHECKOUT || successPaymentStatus) {
    return false
  }
  return (
    isSpotifyCampaignInvalid || hasConflictingOrder || isPreReleaseIncomplete
  )
}

export const useCheckSpotifyCheckoutStepIsAllowed = (): void => {
  const { search, setSearch } = useCustomSearchParams()

  const step = search.get('step')
  const paymentStatus = search.get('paymentStatus')
  const successPaymentStatus = paymentStatus === 'success'
  const spotifyTotalPrice = useAppSelector(spotifyTotalPriceSelector)
  const {
    campaignDetails: spotifyCampaignDetails,
    isPreRelease,
    releaseDate,
    trackFile,
  } = useSpotifyCampaignStatus()
  const { data: currentOrder } = useGetCurrentOrderQuery()
  const { order: conflictingOrder } = useFindConflictingOrder(
    OrderItemType.SpotifyCampaign,
    Number(spotifyCampaignDetails?.id) || 0,
    currentOrder?.id,
  )

  useEffect(() => {
    const isSpotifyCampaignIsNotValid =
      (!spotifyCampaignDetails?.id || spotifyCampaignDetails?.id === 0) &&
      spotifyTotalPrice === null
    const isPreReleaseIncomplete = getIsPreReleaseCheckoutIncomplete(
      isPreRelease,
      releaseDate,
      Boolean(trackFile),
      spotifyCampaignDetails?.genres ?? [],
      spotifyCampaignDetails?.languages ?? [],
      Boolean(spotifyCampaignDetails?.aiUsage),
    )

    const shouldRedirect = getShouldRedirectCheckout(
      step,
      successPaymentStatus,
      isSpotifyCampaignIsNotValid,
      Boolean(conflictingOrder),
      isPreReleaseIncomplete,
    )

    if (shouldRedirect) {
      setSearch({ step: ScheduleSteps.SETTINGS })
    }
  }, [
    spotifyTotalPrice,
    successPaymentStatus,
    step,
    setSearch,
    spotifyCampaignDetails?.id,
    conflictingOrder,
    isPreRelease,
    releaseDate,
    trackFile,
    spotifyCampaignDetails?.genres,
    spotifyCampaignDetails?.languages,
    spotifyCampaignDetails?.aiUsage,
  ])
}
